import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { toast } from 'sonner';
import { 
  Save, 
  Globe, 
  BarChart3, 
  Target, 
  Instagram,
  Info,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';

const AdminMarketing = () => {
  const { token } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    google_analytics_id: '',
    facebook_pixel_id: '',
    google_ads_id: '',
    enabled: {
      google_analytics: false,
      facebook_pixel: false,
      google_ads: false
    }
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/marketing-config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching marketing config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/marketing-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Marketing settings saved successfully!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save marketing settings');
      console.error('Error saving marketing config:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggle = (service) => {
    setConfig(prev => ({
      ...prev,
      enabled: {
        ...prev.enabled,
        [service]: !prev.enabled[service]
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-marketing">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing & Tracking</h1>
          <p className="text-gray-600 mt-1">Configure tracking pixels and analytics for your website</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50"
          data-testid="marketing-save-btn"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          Save Changes
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-4">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-medium text-blue-900">GDPR Compliance</h3>
          <p className="text-sm text-blue-700 mt-1">
            These tracking scripts will only be loaded after users accept marketing cookies via the cookie consent banner.
            This ensures your website is GDPR compliant.
          </p>
        </div>
      </div>

      {/* Google Analytics */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Google Analytics</h2>
                <p className="text-sm text-gray-500">Track website traffic and user behavior</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('google_analytics')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                config.enabled.google_analytics ? 'bg-purple-600' : 'bg-gray-300'
              }`}
              data-testid="toggle-google-analytics"
            >
              <span 
                className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  config.enabled.google_analytics ? 'right-1' : 'left-1'
                }`}
              />
            </button>
          </div>
        </div>
        <div className="p-6 bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Google Analytics Measurement ID
          </label>
          <input
            type="text"
            value={config.google_analytics_id}
            onChange={(e) => handleInputChange('google_analytics_id', e.target.value)}
            placeholder="G-XXXXXXXXXX"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            data-testid="input-google-analytics-id"
          />
          <p className="text-xs text-gray-500 mt-2">
            Find your Measurement ID in Google Analytics 4 → Admin → Data Streams → Your stream
          </p>
        </div>
      </div>

      {/* Facebook Pixel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Facebook Pixel</h2>
                <p className="text-sm text-gray-500">Track conversions from Facebook & Instagram ads</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('facebook_pixel')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                config.enabled.facebook_pixel ? 'bg-purple-600' : 'bg-gray-300'
              }`}
              data-testid="toggle-facebook-pixel"
            >
              <span 
                className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  config.enabled.facebook_pixel ? 'right-1' : 'left-1'
                }`}
              />
            </button>
          </div>
        </div>
        <div className="p-6 bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Facebook Pixel ID
          </label>
          <input
            type="text"
            value={config.facebook_pixel_id}
            onChange={(e) => handleInputChange('facebook_pixel_id', e.target.value)}
            placeholder="XXXXXXXXXXXXXXXX"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            data-testid="input-facebook-pixel-id"
          />
          <p className="text-xs text-gray-500 mt-2">
            Find your Pixel ID in Meta Business Suite → Events Manager → Data Sources
          </p>
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
            <Instagram className="w-4 h-4" />
            <span>Instagram tracking is included via Facebook Pixel</span>
          </div>
        </div>
      </div>

      {/* Google Ads */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Globe className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Google Ads</h2>
                <p className="text-sm text-gray-500">Track conversions from Google Ads campaigns</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('google_ads')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                config.enabled.google_ads ? 'bg-purple-600' : 'bg-gray-300'
              }`}
              data-testid="toggle-google-ads"
            >
              <span 
                className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  config.enabled.google_ads ? 'right-1' : 'left-1'
                }`}
              />
            </button>
          </div>
        </div>
        <div className="p-6 bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Google Ads Conversion ID
          </label>
          <input
            type="text"
            value={config.google_ads_id}
            onChange={(e) => handleInputChange('google_ads_id', e.target.value)}
            placeholder="AW-XXXXXXXXXX"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            data-testid="input-google-ads-id"
          />
          <p className="text-xs text-gray-500 mt-2">
            Find your Conversion ID in Google Ads → Tools → Conversions → Your conversion action
          </p>
        </div>
      </div>

      {/* Status Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Configuration Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatusItem 
            title="Google Analytics"
            enabled={config.enabled.google_analytics}
            configured={!!config.google_analytics_id}
          />
          <StatusItem 
            title="Facebook Pixel"
            enabled={config.enabled.facebook_pixel}
            configured={!!config.facebook_pixel_id}
          />
          <StatusItem 
            title="Google Ads"
            enabled={config.enabled.google_ads}
            configured={!!config.google_ads_id}
          />
        </div>
      </div>
    </div>
  );
};

const StatusItem = ({ title, enabled, configured }) => {
  const isReady = enabled && configured;
  const needsConfig = enabled && !configured;
  
  return (
    <div className={`p-4 rounded-lg border ${
      isReady ? 'bg-green-50 border-green-200' : 
      needsConfig ? 'bg-yellow-50 border-yellow-200' : 
      'bg-gray-50 border-gray-200'
    }`}>
      <div className="flex items-center gap-2">
        {isReady ? (
          <CheckCircle className="w-5 h-5 text-green-600" />
        ) : needsConfig ? (
          <AlertCircle className="w-5 h-5 text-yellow-600" />
        ) : (
          <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
        )}
        <span className={`font-medium ${
          isReady ? 'text-green-900' : 
          needsConfig ? 'text-yellow-900' : 
          'text-gray-600'
        }`}>
          {title}
        </span>
      </div>
      <p className={`text-xs mt-1 ${
        isReady ? 'text-green-700' : 
        needsConfig ? 'text-yellow-700' : 
        'text-gray-500'
      }`}>
        {isReady ? 'Ready to track' : needsConfig ? 'Missing ID' : 'Disabled'}
      </p>
    </div>
  );
};

export default AdminMarketing;
